import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Button from './ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/Dialog';
import { Ruler, Info } from 'lucide-react';
import styled from 'styled-components';

const StyledDialogContent = styled(DialogContent)`
  max-width: 48rem;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing['2xl']};
  padding: ${props => props.theme.spacing.lg} 0;
`;

const CustomSizeNote = styled.div`
  background-color: ${props => props.theme.colors.accent.main}80;
  border: 1px solid ${props => props.theme.colors.primary.main}33;
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  gap: ${props => props.theme.spacing.md};
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => props.theme.colors.primary.main};
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
  
  div {
    h4 {
      font-weight: ${props => props.theme.typography.fontWeight.medium};
      color: ${props => props.theme.colors.text.primary};
      margin-bottom: ${props => props.theme.spacing.xs};
    }
    
    p {
      font-size: ${props => props.theme.typography.fontSize.sm};
      color: ${props => props.theme.colors.text.muted};
      margin: 0;
    }
  }
`;

const TableSection = styled.div`
  h3 {
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: ${props => props.theme.spacing.lg};
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  font-size: ${props => props.theme.typography.fontSize.sm};
  
  thead {
    tr {
      background-color: ${props => props.theme.colors.background.muted};
      
      th {
        padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
        text-align: left;
        font-weight: ${props => props.theme.typography.fontWeight.semibold};
        color: ${props => props.theme.colors.text.primary};
        
        &:first-child {
          border-top-left-radius: ${props => props.theme.borderRadius.lg};
        }
        
        &:last-child {
          border-top-right-radius: ${props => props.theme.borderRadius.lg};
        }
      }
    }
  }
  
  tbody {
    tr {
      border-bottom: 1px solid ${props => props.theme.colors.border.main};
      transition: ${props => props.theme.transitions.default};
      
      &:last-child {
        border-bottom: none;
      }
      
      &:hover {
        background-color: ${props => props.theme.colors.accent.main}4D;
      }
      
      td {
        padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
        
        &:first-child {
          font-weight: ${props => props.theme.typography.fontWeight.medium};
          color: ${props => props.theme.colors.text.primary};
        }
        
        &:not(:first-child) {
          color: ${props => props.theme.colors.text.muted};
        }
      }
    }
  }
`;

const HowToMeasure = styled.div`
  background-color: ${props => props.theme.colors.background.card};
  border: 1px solid ${props => props.theme.colors.border.main};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing['2xl']};
  
  h3 {
    font-family: ${props => props.theme.typography.fontFamily.display};
    font-size: ${props => props.theme.typography.fontSize.xl};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: ${props => props.theme.spacing.lg};
  }
`;

const MeasureSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const MeasureStep = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  
  .step-number {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: ${props => props.theme.colors.primary.main}1A;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.colors.primary.main};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    flex-shrink: 0;
  }
  
  div {
    h4 {
      font-weight: ${props => props.theme.typography.fontWeight.medium};
      color: ${props => props.theme.colors.text.primary};
      margin-bottom: ${props => props.theme.spacing.xs};
    }
    
    p {
      font-size: ${props => props.theme.typography.fontSize.sm};
      color: ${props => props.theme.colors.text.muted};
      margin: 0;
    }
  }
`;

const SizeChartModal = () => {
  const { t } = useLanguage();

  const sizeData = [
    { size: 'XS (0-2)', bust: '32-33"', waist: '24-25"', hips: '34-35"' },
    { size: 'S (4-6)', bust: '34-35"', waist: '26-27"', hips: '36-37"' },
    { size: 'M (8-10)', bust: '36-37"', waist: '28-29"', hips: '38-39"' },
    { size: 'L (12-14)', bust: '38-40"', waist: '30-32"', hips: '40-42"' },
    { size: 'XL (16-18)', bust: '41-43"', waist: '33-35"', hips: '43-45"' },
    { size: '2XL (20-22)', bust: '44-46"', waist: '36-38"', hips: '46-48"' },
  ];

  const sizeDataMetric = [
    { size: 'XS (0-2)', bust: '81-84 cm', waist: '61-64 cm', hips: '86-89 cm' },
    { size: 'S (4-6)', bust: '86-89 cm', waist: '66-69 cm', hips: '91-94 cm' },
    { size: 'M (8-10)', bust: '91-94 cm', waist: '71-74 cm', hips: '97-99 cm' },
    { size: 'L (12-14)', bust: '97-102 cm', waist: '76-81 cm', hips: '102-107 cm' },
    { size: 'XL (16-18)', bust: '104-109 cm', waist: '84-89 cm', hips: '109-114 cm' },
    { size: '2XL (20-22)', bust: '112-117 cm', waist: '91-97 cm', hips: '117-122 cm' },
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="lg" style={{ gap: '0.5rem' }}>
          <Ruler style={{ width: '1.25rem', height: '1.25rem' }} />
          {t('sizechart.button')}
        </Button>
      </DialogTrigger>
      <StyledDialogContent>
        <DialogHeader>
          <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Ruler style={{ width: '1.5rem', height: '1.5rem', color: 'hsl(200, 80%, 60%)' }} />
            {t('sizechart.title')}
          </DialogTitle>
        </DialogHeader>

        <ModalContent>
          <CustomSizeNote>
            <Info />
            <div>
              <h4>{t('sizechart.customNote')}</h4>
              <p>{t('sizechart.customDesc')}</p>
            </div>
          </CustomSizeNote>

          <TableSection>
            <h3>{t('sizechart.imperial')}</h3>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>{t('sizechart.size')}</th>
                    <th>{t('sizechart.bust')}</th>
                    <th>{t('sizechart.waist')}</th>
                    <th>{t('sizechart.hips')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sizeData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.size}</td>
                      <td>{row.bust}</td>
                      <td>{row.waist}</td>
                      <td>{row.hips}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </TableSection>

          <TableSection>
            <h3>{t('sizechart.metric')}</h3>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>{t('sizechart.size')}</th>
                    <th>{t('sizechart.bust')}</th>
                    <th>{t('sizechart.waist')}</th>
                    <th>{t('sizechart.hips')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sizeDataMetric.map((row, index) => (
                    <tr key={index}>
                      <td>{row.size}</td>
                      <td>{row.bust}</td>
                      <td>{row.waist}</td>
                      <td>{row.hips}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </TableSection>

          <HowToMeasure>
            <h3>{t('sizechart.howTo')}</h3>
            <MeasureSteps>
              <MeasureStep>
                <div className="step-number">1</div>
                <div>
                  <h4>{t('sizechart.bust')}</h4>
                  <p>{t('sizechart.bustHow')}</p>
                </div>
              </MeasureStep>
              <MeasureStep>
                <div className="step-number">2</div>
                <div>
                  <h4>{t('sizechart.waist')}</h4>
                  <p>{t('sizechart.waistHow')}</p>
                </div>
              </MeasureStep>
              <MeasureStep>
                <div className="step-number">3</div>
                <div>
                  <h4>{t('sizechart.hips')}</h4>
                  <p>{t('sizechart.hipsHow')}</p>
                </div>
              </MeasureStep>
              <MeasureStep>
                <div className="step-number">4</div>
                <div>
                  <h4>{t('sizechart.height')}</h4>
                  <p>{t('sizechart.heightHow')}</p>
                </div>
              </MeasureStep>
            </MeasureSteps>
          </HowToMeasure>
        </ModalContent>
      </StyledDialogContent>
    </Dialog>
  );
};

export default SizeChartModal;
